import { useState, useCallback } from 'react'
import {
  headlessListSessions,
  headlessCreateSession,
  headlessGetSession,
  headlessUpdateSession,
  headlessDeleteSession,
} from 'src/api/headless'

const useSession = () => {
  const [sessions, setSessions] = useState([])
  const [totalSessions, setTotalSessions] = useState(0)
  const [currentSession, setCurrentSession] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const fetchSessions = useCallback(async (skip = 0, limit = 20) => {
    try {
      // Only show loading spinner on initial fetch, not background polls
      if (!loaded) setLoading(true)
      const data = await headlessListSessions(skip, limit)
      setSessions(data.sessions || [])
      setTotalSessions(data.total || 0)
    } catch (e) {
      // silent
    } finally {
      setLoading(false)
      setLoaded(true)
    }
  }, [loaded])

  const createSession = useCallback(async data => {
    try {
      const result = await headlessCreateSession(data)
      setCurrentSession(result)
      return result
    } catch (e) {
      // silent
      return null
    }
  }, [])

  const updateSession = useCallback(async (id, updates) => {
    try {
      const result = await headlessUpdateSession(id, updates)
      setCurrentSession(prev =>
        prev && prev.session_id === id ? { ...prev, ...updates } : prev,
      )
      return result
    } catch (e) {
      // silent
      return null
    }
  }, [])

  const resumeSession = useCallback(async id => {
    try {
      setLoading(true)
      const data = await headlessGetSession(id)
      setCurrentSession(data)
      return data
    } catch (e) {
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const cancelSession = useCallback(
    async id => {
      try {
        await headlessDeleteSession(id)
        await fetchSessions()
        if (currentSession && currentSession.session_id === id) {
          setCurrentSession(null)
        }
      } catch (e) {
        // silent
      }
    },
    [currentSession, fetchSessions],
  )

  const clearCurrentSession = useCallback(() => {
    setCurrentSession(null)
  }, [])

  return {
    sessions,
    totalSessions,
    currentSession,
    loading,
    loaded,
    fetchSessions,
    createSession,
    updateSession,
    resumeSession,
    cancelSession,
    clearCurrentSession,
  }
}

export default useSession
